import { Avatar, Box, IconButton, Typography } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import { getLibraryPageTokens } from '../getLibraryPageTokens';
import { formatLibraryCardStatus } from '../libraryStatus';
import { LIBRARY_PAGE_LAYOUT_SIZES } from '../libraryTokens';
import { LibraryPlatformIcon } from './LibraryPlatformIcon';

export interface LibraryExperimentCardOwner {
  display_name: string | null;
  initials: string | null;
}

export interface LibraryExperimentCardProps {
  title: string;
  description?: string;
  typeLabel: string;
  statusLabel: string;
  owner?: LibraryExperimentCardOwner;
  createdAtLabel?: string;
  onClick: () => void;
  showDelete?: boolean;
  onDelete?: () => void;
}

export function LibraryExperimentCard({
  title,
  description,
  typeLabel,
  statusLabel,
  owner,
  createdAtLabel,
  onClick,
  showDelete = false,
  onDelete,
}: LibraryExperimentCardProps): JSX.Element {
  const theme = useTheme();
  const tokens = getLibraryPageTokens(theme);
  const ownerLabel = owner?.display_name?.trim() || null;
  const ownerInitials = owner?.initials?.trim() || '?';

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: LIBRARY_PAGE_LAYOUT_SIZES.cardMinHeight,
        height: '100%',
        borderRadius: `${LIBRARY_PAGE_LAYOUT_SIZES.cardRadius}px`,
        border: `1px solid ${tokens.cardBorder}`,
        bgcolor: tokens.cardBackground,
        transition:
          'background-color 120ms cubic-bezier(0.16, 1, 0.3, 1), border-color 120ms cubic-bezier(0.16, 1, 0.3, 1)',
        '&:hover': {
          bgcolor: tokens.cardHoverBackground,
        },
      }}
    >
      {showDelete && onDelete ? (
        <IconButton
          size="small"
          aria-label="Delete experiment"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: tokens.pageSubtitleColor,
            '&:hover': { color: theme.palette.error.main },
          }}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      ) : null}

      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          textAlign: 'left',
          flex: 1,
          width: '100%',
          height: '100%',
          p: `${LIBRARY_PAGE_LAYOUT_SIZES.cardPadding}px`,
          border: 'none',
          bgcolor: 'transparent',
          cursor: 'pointer',
        }}
      >
        <LibraryPlatformIcon />

        <Typography
          component="h3"
          sx={{
            mt: 3,
            fontSize: '20px',
            fontWeight: 500,
            lineHeight: '28px',
            letterSpacing: '-0.4492px',
            color: tokens.pageHeadingColor,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            pr: showDelete ? 3 : 0,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            mt: 1.5,
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '-0.1504px',
            color: tokens.statusTextColor,
            textTransform: 'uppercase',
          }}
        >
          {formatLibraryCardStatus(statusLabel)}
        </Typography>

        {description ? (
          <Typography
            sx={{
              mt: 1.5,
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '22.75px',
              letterSpacing: '-0.1504px',
              color: tokens.bodySecondaryColor,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description}
          </Typography>
        ) : null}

        <Box sx={{ mt: 'auto', pt: 2.5 }}>
          {ownerLabel ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Avatar
                sx={{
                  width: LIBRARY_PAGE_LAYOUT_SIZES.ownerAvatarSize,
                  height: LIBRARY_PAGE_LAYOUT_SIZES.ownerAvatarSize,
                  fontSize: 14,
                  bgcolor: theme.palette.library.ownerAvatarBackground,
                  color: theme.palette.library.ownerAvatarColor,
                  flexShrink: 0,
                }}
              >
                {ownerInitials}
              </Avatar>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: tokens.filterLabelColor,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {ownerLabel}
              </Typography>
            </Box>
          ) : null}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: ownerLabel ? 1.5 : 0 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: LIBRARY_PAGE_LAYOUT_SIZES.tagChipHeight,
                px: 1.5,
                borderRadius: `${LIBRARY_PAGE_LAYOUT_SIZES.tagChipRadius}px`,
                bgcolor: tokens.tagChipBg,
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  color: tokens.tagChipColor,
                }}
              >
                {typeLabel}
              </Typography>
            </Box>
            {createdAtLabel ? (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: LIBRARY_PAGE_LAYOUT_SIZES.tagChipHeight,
                  px: 1.5,
                  borderRadius: `${LIBRARY_PAGE_LAYOUT_SIZES.tagChipRadius}px`,
                  bgcolor: tokens.tagChipBg,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '16px',
                    color: tokens.tagChipColor,
                  }}
                >
                  {createdAtLabel}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
